import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@features/auth/store/authStore';
import appInsightsService from '../../services/appInsights/appInsights.service';

const baseURL = import.meta.env.VITE_API_BASE_URL || '/api';

/**
 * Generates a per-request correlation id. Prefers the native crypto.randomUUID
 * (available over https and on localhost); falls back to a manual UUID v4 for
 * insecure contexts where it's undefined.
 */
function generateCorrelationId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch { /* fall through to manual generation */ }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export const apiClient = axios.create({
  baseURL,
  // Must be >= backend's ALUDRA_API_TIMEOUT (60s) so the frontend never
  // gives up before the backend does. Previously 30s — caused false
  // "timeout" errors when Aludra was slow, making users retry and
  // eventually hit the rate limiter.
  timeout: 65000, // 65 seconds
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Para cookies de refresh token
});

// Función para limpiar autenticación y redirigir al login
const handleAuthFailure = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('sessionId');
  localStorage.removeItem('selectedCompanyId');
  localStorage.removeItem('authUser');
  localStorage.removeItem('auth-storage');
  localStorage.removeItem('invoice-filter-storage');

  // Redirigir al login con indicador de sesión expirada
  if (window.location.pathname !== '/login') {
    window.location.href = '/login?expired=1';
  }
};

function shouldKeepSessionOnRefreshFailure(error: unknown): boolean {
  const axiosError = error as AxiosError | undefined;
  const status = axiosError?.response?.status;

  return status === 503 || status === 440 || !axiosError?.response;
}

// Single shared refresh promise — prevents race conditions
let refreshPromise: Promise<string> | null = null;

// Request interceptor - Agregar token y sessionId a las peticiones
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const accessToken = localStorage.getItem('accessToken');
    const sessionId = localStorage.getItem('sessionId');

    if (accessToken && config.headers) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    // Send sessionId in header for multi-session support
    if (sessionId && config.headers) {
      config.headers['x-session-id'] = sessionId;
    }

    // Correlation id: one per request, propagated to the BFF (and on to Aludra)
    // so a single id traces the whole chain. The BFF echoes it back in the
    // response header for support reports / Application Insights filtering.
    if (config.headers) {
      config.headers['x-correlation-id'] = generateCorrelationId();
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - Manejar refresh token automáticamente
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // 503 = Service unavailable (Redis down, etc.) — do NOT logout, just propagate the error
    // The user's session is still valid, the server just can't reach Redis momentarily
    if (error.response?.status === 503) {
      return Promise.reject(error);
    }

    // 440 = external service session expired/unavailable.
    // Keep the portal session alive so the UI can degrade gracefully.
    if (error.response?.status === 440) {
      return Promise.reject(error);
    }

    // 401 = Our JWT expired — try refresh
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/login') &&
      !originalRequest.url?.includes('/auth/refresh')
    ) {
      originalRequest._retry = true;

      // If a refresh is already in progress, ALL requests wait on the same promise
      // No race condition: refreshPromise is set synchronously before any await
      if (!refreshPromise) {
        refreshPromise = (async () => {
          try {
            const { data } = await apiClient.post('/auth/refresh');
            const { accessToken } = data;

            try { localStorage.setItem('accessToken', accessToken); } catch { /* storage full — Zustand state is enough */ }
            useAuthStore.setState({ accessToken });

            return accessToken;
          } catch (refreshError) {
            if (!shouldKeepSessionOnRefreshFailure(refreshError)) {
              handleAuthFailure();
            }
            throw refreshError;
          } finally {
            refreshPromise = null;
          }
        })();
      }

      try {
        const token = await refreshPromise;
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${token}`;
        }
        return apiClient(originalRequest);
      } catch (err) {
        return Promise.reject(err);
      }
    }

    // 401 on /auth/refresh → session is truly dead, clean up and redirect
    // 401 on /auth/login → just wrong credentials, do NOT clear storage/redirect
    //   (the login form handles this error and shows the message to the user)
    if (error.response?.status === 401 && originalRequest.url?.includes('/auth/refresh')) {
      handleAuthFailure();
      return Promise.reject(error);
    }

    // Track failed API calls in Application Insights
    const correlationId =
      (error.config?.headers?.['x-correlation-id'] as string | undefined) ||
      (error.response?.headers?.['x-correlation-id'] as string | undefined) ||
      'none';
    try {
      appInsightsService.trackException(error, {
        url: error.config?.url || 'unknown',
        method: error.config?.method?.toUpperCase() || 'unknown',
        status: String(error.response?.status || 0),
        correlationId,
      });
    } catch { /* telemetry must never break the app */ }

    return Promise.reject(error);
  }
);
